import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { investorService, DsrCaseCreateDto } from '../../../services/investor.service';
import { PremiumSelect } from '../../../components/PremiumSelect/PremiumSelect';
import { FiChevronLeft, FiClock, FiPaperclip, FiTrash2, FiCheck } from 'react-icons/fi';
import '../InvestorProfile/InvestorProfile.scss';
import './DsrCenter.scss';

const ChevronLeftIcon = FiChevronLeft as any;
const ClockIcon = FiClock as any;
const PaperclipIcon = FiPaperclip as any;
const TrashIcon = FiTrash2 as any;
const CheckIcon = FiCheck as any;

const REQUEST_TYPES = [
  'ACCESS',
  'DATA_COPY',
  'CORRECTION',
  'ERASURE',
  'CONSENT_WITHDRAWAL',
  'GRIEVANCE',
  'NOMINATION',
  'MARKETING_OPTOUT',
  'COOKIE_TRACKING',
  'RESTRICT',
  'OTHER'
];

// Maps the investor's Country of Residence to a supported DSR jurisdiction code.
const COUNTRY_TO_JURISDICTION: { [key: string]: string } = {
  'INDIA': 'INDIA',
  'CANADA': 'CANADA',
  'UK': 'UK',
  'UNITED KINGDOM': 'UK',
  'GREAT BRITAIN': 'UK',
  'UAE': 'UAE',
  'UNITED ARAB EMIRATES': 'UAE',
  'HONG KONG': 'HONG_KONG',
  'HONGKONG': 'HONG_KONG',
  'SINGAPORE': 'SINGAPORE'
};

const deriveJurisdiction = (country?: string): string => {
  if (!country) return 'INDIA';
  return COUNTRY_TO_JURISDICTION[country.trim().toUpperCase()] || 'INDIA';
};

// Task E: field renamed "Facilon Product" (label only — options unchanged per user 2026-08-23).
const DATA_AREAS: { code: string; label: string }[] = [
  { code: 'STATUS', label: 'Facilon Status' }
];

const DECLARATION_TEXT =
  'I confirm that this request is being submitted through my Facilon Investor Account. I understand that ' +
  'Facilon may need to verify my identity, clarify the request, or coordinate with the relevant Service ' +
  'Provider before taking action. I also understand that certain requests may be subject to legal, ' +
  'regulatory, contractual, security, audit, or retention requirements.';

export const DsrCenter: React.FC = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [supportingFiles, setSupportingFiles] = useState<File[]>([]);
  const [dataAreas, setDataAreas] = useState<string[]>(['STATUS']);
  const [declaration, setDeclaration] = useState(false);
  const [form, setForm] = useState<DsrCaseCreateDto>({
    requestType: 'ACCESS',
    jurisdiction: 'INDIA',
    requestDescription: '',
    requesterName: '',
    requesterEmail: '',
    requesterPhone: '',
    requesterRole: 'INVESTOR'
  });

  const handleFieldChange = (field: keyof DsrCaseCreateDto, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prevErr) => {
        const next = { ...prevErr };
        delete next[field];
        return next;
      });
    }
  };

  const toggleDataArea = (code: string) => {
    setDataAreas((prev) => {
      const updated = prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code];
      if (errors.dataAreas) {
        setErrors((prevErr) => {
          const next = { ...prevErr };
          delete next.dataAreas;
          return next;
        });
      }
      return updated;
    });
  };

  const MAX_FILES = 10;

  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const accepted: File[] = [];
    for (const f of Array.from(list)) {
      const ext = f.name.split('.').pop()?.toLowerCase();
      if (!ext || !['pdf', 'jpg', 'jpeg'].includes(ext)) {
        alert(`${f.name}: only PDF/JPG/JPEG files are allowed.`);
        continue;
      }
      if (f.size > 5 * 1024 * 1024) {
        alert(`${f.name}: exceeds the 5MB limit.`);
        continue;
      }
      accepted.push(f);
    }
    setSupportingFiles((prev) => {
      const combined = [...prev];
      for (const f of accepted) {
        if (!combined.some((c) => c.name === f.name && c.size === f.size)) {
          combined.push(f);
        }
      }
      if (combined.length > MAX_FILES) {
        alert(`You can attach at most ${MAX_FILES} files.`);
      }
      return combined.slice(0, MAX_FILES);
    });
  };

  const removeFile = (idx: number) =>
    setSupportingFiles((prev) => prev.filter((_, i) => i !== idx));

  useEffect(() => {
    prefillFromProfile();
  }, []);

  const prefillFromProfile = async () => {
    try {
      const data = await investorService.getDashboard();
      const inv = data?.investor;
      if (inv) {
        const fullName = [inv.firstName, inv.middleName, inv.lastName]
          .filter((p) => p && p.trim())
          .join(' ') || inv.name || '';
        setForm((prev) => ({
          ...prev,
          requesterName: fullName || prev.requesterName,
          requesterEmail: inv.email || prev.requesterEmail,
          requesterPhone: inv.mobileNumber || prev.requesterPhone,
          jurisdiction: deriveJurisdiction(inv.countryOfResidence)
        }));
      }
    } catch (error) {
      console.error('[DsrCenter] Failed prefilling requester details from profile:', error);
    }
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!form.requestType || !form.requestType.trim()) {
      newErrors.requestType = 'Please select a right.';
    }
    if (!form.jurisdiction || !form.jurisdiction.trim()) {
      newErrors.jurisdiction = 'Please select a jurisdiction.';
    }
    if (!form.requesterName || !form.requesterName.trim()) {
      newErrors.requesterName = 'Requester Name is required.';
    }
    if (!form.requesterEmail || !form.requesterEmail.trim()) {
      newErrors.requesterEmail = 'Requester Email is required.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.requesterEmail)) {
        newErrors.requesterEmail = 'Please enter a valid email address.';
      }
    }
    if (!form.requestDescription || !form.requestDescription.trim()) {
      newErrors.requestDescription = 'Request Description is required.';
    } else if (form.requestDescription.trim().length < 20) {
      newErrors.requestDescription = 'Please enter at least 20 characters.';
    }
    if (dataAreas.length === 0) {
      newErrors.dataAreas = 'Please select at least one Facilon Product.';
    }
    if (!declaration) {
      newErrors.declaration = 'Please confirm the declaration.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    try {
      setSaving(true);
      await investorService.submitDsrCase({ ...form, dataArea: dataAreas.join(',') }, supportingFiles);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('[DsrCenter] Failed submitting DSR case:', error);
      alert('Failed to submit DSR request. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="facilon-dashboard-wrapper">
      <main className="container-fluid dashboard-container-main p-0">
        <div className="investor-profile dsr-page">

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6 shadow-sm">
            {/* Blue Header Banner */}
            <div className="p-3 flex justify-between items-center text-white" style={{ background: 'linear-gradient(90deg, rgba(5, 47, 85, 1), rgba(7, 87, 155, 1)), radial-gradient(circle at 82% 20%, rgba(28, 166, 217, 1), transparent 34%)' }}>

              <div>
                <h2 className="m-0 text-base font-bold text-white tracking-tight">Data Subject Rights Center</h2>
                <p className="m-0 text-[11.5px] font-normal text-white/80 mt-0.5">Manage your data requests and privacy preferences.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center bg-white text-[#3e6f7c] border-0 text-[11px] font-bold h-[30px] px-3.5 rounded-md transition-colors hover:bg-white/90 cursor-pointer shadow-sm text-decoration-none"
                  onClick={() => navigate('/investor/dsr-center/requests')}
                >
                  <ClockIcon size={12} className="me-1.5 flex-shrink-0" />
                  Request Status
                </button>
                <button
                  type="button"
                  className="inline-flex items-center bg-white/10 border border-white/20 text-white text-[11px] font-semibold h-[30px] px-3 rounded-md transition-colors hover:bg-white/20 hover:border-white/30 cursor-pointer text-decoration-none"
                  onClick={() => navigate('/investor/dashboard')}
                >
                  <ChevronLeftIcon size={12} className="me-1 flex-shrink-0" />
                  Back
                </button>
              </div>
            </div>

            {/* Dsr Card Body */}
            <div className="p-3 bg-white">
              <form className="bg-transparent border-0 rounded-none mb-0 pt-2.5 p-0" onSubmit={onSubmit} noValidate>
                <div className="investor-profile__grid">
                  <div className="form-group">
                    <label htmlFor="dsr-request-type">Right Exercised <span className="text-danger">*</span></label>
                    <PremiumSelect
                      value={form.requestType}
                      onChange={(val) => handleFieldChange('requestType', val)}
                      options={REQUEST_TYPES.map((type) => ({ value: type, label: type }))}
                      placeholder="Select Right"
                    />
                    {errors.requestType && <span className="text-[10px] text-red-500 font-semibold mt-1 block">{errors.requestType}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="dsr-jurisdiction">Jurisdiction <span className="text-[10px] italic text-muted">(from your profile)</span></label>
                    <input
                      id="dsr-jurisdiction"
                      className="form-control"
                      value={form.jurisdiction}
                      readOnly
                      style={{ backgroundColor: '#f8fafc', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="dsr-role">Role <span className="text-[10px] italic text-muted">(from your profile)</span></label>
                    <input
                      id="dsr-role"
                      className="form-control"
                      value={form.requesterRole || ''}
                      readOnly
                      style={{ backgroundColor: '#f8fafc', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="dsr-requester-name">Requester Name <span className="text-[10px] italic text-muted">(from your profile)</span></label>
                    <input
                      id="dsr-requester-name"
                      className="form-control"
                      value={form.requesterName}
                      readOnly
                      style={{ backgroundColor: '#f8fafc', cursor: 'not-allowed' }}
                    />
                    {errors.requesterName && <span className="text-[10px] text-red-500 font-semibold mt-1 block">{errors.requesterName}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="dsr-requester-email">Requester Email <span className="text-[10px] italic text-muted">(from your profile)</span></label>
                    <input
                      id="dsr-requester-email"
                      type="email"
                      className="form-control"
                      value={form.requesterEmail}
                      readOnly
                      style={{ backgroundColor: '#f8fafc', cursor: 'not-allowed' }}
                    />
                    {errors.requesterEmail && <span className="text-[10px] text-red-500 font-semibold mt-1 block">{errors.requesterEmail}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="dsr-requester-phone">Requester Phone <span className="text-[10px] italic text-muted">(from your profile)</span></label>
                    <input
                      id="dsr-requester-phone"
                      className="form-control"
                      value={form.requesterPhone || ''}
                      readOnly
                      style={{ backgroundColor: '#f8fafc', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="dsr-supporting-file">Supporting Evidence <span className="text-[10px] italic text-muted">(pdf/jpg/jpeg, max 5MB each — multiple allowed)</span></label>
                    <div className="mt-1">
                      <input
                        id="dsr-supporting-file"
                        type="file"
                        multiple
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg"
                        onChange={(e) => {
                          addFiles(e.target.files);
                          e.target.value = '';
                        }}
                      />

                      <label
                        htmlFor="dsr-supporting-file"
                        className="inline-flex items-center justify-center border border-[#3e6f7c] text-[#3e6f7c] font-bold text-[10px] h-[28px] px-3.5 rounded-md cursor-pointer transition-colors bg-transparent hover:bg-[#3e6f7c]/5 select-none w-auto max-w-max"
                      >
                        {supportingFiles.length > 0 ? 'Add More Files' : 'Choose Files'}
                      </label>

                      {supportingFiles.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {supportingFiles.map((file, idx) => (
                            <div key={idx} className="inline-flex items-center gap-1.5 p-1.5 bg-slate-50 border border-slate-100 rounded-md max-w-max">
                              <PaperclipIcon size={14} className="text-slate-400 flex-shrink-0" />
                              <span className="text-[11px] font-semibold text-slate-600 truncate max-w-[160px]">{file.name}</span>
                              <button
                                type="button"
                                className="flex items-center justify-center w-6 h-6 rounded-md hover:bg-red-50 text-red-500 hover:text-red-700 transition-colors border-0 cursor-pointer bg-transparent"
                                onClick={() => removeFile(idx)}
                              >
                                <TrashIcon size={14} className="flex-shrink-0" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group form-group--full">
                    <label htmlFor="dsr-request-description">Request Description <span className="text-danger">*</span></label>
                    <textarea
                      id="dsr-request-description"
                      className="form-control"
                      rows={4}
                      value={form.requestDescription}
                      onChange={(e) => handleFieldChange('requestDescription', e.target.value)}
                    />
                    {errors.requestDescription && <span className="text-[10px] text-red-500 font-semibold mt-1 block">{errors.requestDescription}</span>}
                  </div>

                  <div className="form-group form-group--full">
                    <label>Facilon Product <span className="text-danger">*</span></label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {DATA_AREAS.map((area) => {
                        const isChecked = dataAreas.includes(area.code);
                        return (
                          <div className={`form-group--checkbox ${isChecked ? 'checked' : ''}`} key={area.code}>
                            <label htmlFor={`dsr-data-area-${area.code}`}>
                              <input
                                type="checkbox"
                                id={`dsr-data-area-${area.code}`}
                                checked={isChecked}
                                onChange={() => toggleDataArea(area.code)}
                              />
                              {area.label}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                    {errors.dataAreas && <span className="text-[10px] text-red-500 font-semibold mt-1.5 block">{errors.dataAreas}</span>}
                  </div>



                  <div className="form-group form-group--full">
                    <div className={`form-group--checkbox ${declaration ? 'checked' : ''}`}>
                      <label htmlFor="dsr-declaration">
                        <input
                          type="checkbox"
                          id="dsr-declaration"
                          checked={declaration}
                          onChange={(e) => {
                            setDeclaration(e.target.checked);
                            if (errors.declaration) {
                              setErrors((prevErr) => {
                                const next = { ...prevErr };
                                delete next.declaration;
                                return next;
                              });
                            }
                          }}
                        />
                        <span className="font-bold">{DECLARATION_TEXT}</span>
                      </label>
                    </div>
                    {errors.declaration && <span className="text-[10px] text-red-500 font-semibold mt-1 block">{errors.declaration}</span>}
                  </div>

                  <div className="form-group form-group--full">
                    <button type="submit" className="btn-save" disabled={saving}>
                      {saving ? 'Submitting...' : 'Submit DSR Request'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>

      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-xl flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
              <CheckIcon size={32} className="text-emerald-600 flex-shrink-0" />
            </div>
            <h3 className="text-3xl font-bold text-slate-800 mb-2">Request Submitted</h3>
            <p className="text-[12px] text-slate-500 mb-3 max-w-[280px]">
              Your Data Subject Rights request has been submitted successfully and is now being processed.
            </p>
            <button
              type="button"
              className="w-full py-2 bg-[#3e6f7c] hover:bg-[#355f69] text-white text-[12px] font-bold rounded-md transition-colors cursor-pointer border-0 shadow-sm"
              onClick={() => {
                setShowSuccessModal(false);
                navigate('/investor/dsr-center/requests');
              }}
            >
              View Requests
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
